// ============================================================================
// banners.js — transient theatre: grimdark announcer lines, the WARLORD SLAIN
// banner, kill milestones, the TITANFALL flash + screen desaturation, and the
// victory/defeat screens. Event-driven; a tiny self-managed queue (no timers).
// ============================================================================

import { EV } from '../core/events.js';

const CSS = `
#banner-layer{position:fixed;inset:0;pointer-events:none;z-index:20;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding-top:20vh;}
.banner{font-family:'Trebuchet MS',system-ui,sans-serif;text-align:center;margin-bottom:10px;will-change:transform,opacity;}
.banner .big{font-size:clamp(28px,6vw,64px);font-weight:900;letter-spacing:.14em;}
.banner .sub{font-size:clamp(13px,2vw,20px);letter-spacing:.16em;margin-top:6px;font-style:italic;}
.banner.good .big{color:#ffd07a;text-shadow:0 0 26px rgba(255,140,40,.7),0 3px 8px #000;}
.banner.bad .big{color:#ff6a4a;text-shadow:0 0 26px rgba(200,40,20,.7),0 3px 8px #000;}
.banner.neutral .big{color:#e6ddd2;text-shadow:0 0 20px rgba(0,0,0,.9),0 3px 8px #000;}
.banner .sub{color:#c9beb0;}
#titan-flash{position:fixed;inset:0;pointer-events:none;z-index:19;background:radial-gradient(circle at 50% 55%,rgba(255,120,30,.35),rgba(0,0,0,0) 60%);opacity:0;transition:opacity .2s;}
#endscreen{position:fixed;inset:0;z-index:40;display:none;flex-direction:column;align-items:center;justify-content:center;background:radial-gradient(circle at 50% 40%,rgba(20,10,6,.86),rgba(3,2,2,.96));}
#endscreen h1{font-size:clamp(46px,10vw,120px);font-weight:900;letter-spacing:.2em;}
#endscreen .s{margin-top:14px;color:#b9b2a8;font-size:16px;letter-spacing:.14em;max-width:640px;text-align:center;line-height:1.7;}
#endscreen .again{margin-top:36px;pointer-events:auto;cursor:pointer;padding:12px 34px;border:1px solid rgba(255,106,26,.4);background:rgba(8,6,5,.6);color:#ffb257;letter-spacing:.28em;font-size:14px;font-weight:700;}
#endscreen .again:hover{background:rgba(255,106,26,.18);color:#fff;}
#endscreen.win h1{color:#ffd07a;text-shadow:0 0 40px rgba(255,140,40,.7);}
#endscreen.lose h1{color:#b81f1f;text-shadow:0 0 40px rgba(200,20,20,.6);}
`;

export class Banners {
  constructor(hudRoot, world) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    hudRoot.insertAdjacentHTML('beforeend', `
      <div id="titan-flash"></div>
      <div id="banner-layer"></div>
      <div id="endscreen"><h1></h1><div class="s"></div><div class="again">FIGHT AGAIN</div></div>
    `);
    this.layer = hudRoot.querySelector('#banner-layer');
    this.flash = hudRoot.querySelector('#titan-flash');
    this.end = hudRoot.querySelector('#endscreen');
    this.endH = this.end.querySelector('h1');
    this.endS = this.end.querySelector('.s');
    this.end.querySelector('.again').addEventListener('click', () => location.reload());

    this.active = []; // {el,t,dur}
    this._app = document.getElementById('app');

    const ev = world.events;
    ev.on(EV.ANNOUNCE, ({ text, tone }) => this.show(text, '', tone || 'neutral', 3.2, 0.7));
    ev.on(EV.WARLORD_ENGAGED, ({ warlord }) =>
      this.show('A MAGMAR WARLORD TAKES THE FIELD', warlord.warlord ? `${warlord.warlord.name}, ${warlord.warlord.title}` : '', 'bad', 3.6, 1.0));
    ev.on(EV.WARLORD_SLAIN, ({ warlord }) =>
      this.show('WARLORD SLAIN', warlord && warlord.warlord ? warlord.warlord.name : '', 'good', 3.6, 1.2));
    ev.on(EV.MILESTONE, ({ total }) => this.show(`${total} SLAIN`, 'The ash drinks deep.', 'good', 2.6, 1.0));
    ev.on(EV.TITANFALL, () => { this.show('TITANFALL', 'Masamune erupts.', 'good', 2.4, 1.3); this._flash(); });
    // The campaign results screen (GameManager) is the real end card now; here we
    // just flash a transient banner before it appears.
    ev.on(EV.VICTORY, () => this.show('VICTORY', '', 'good', 1.6, 1.4));
    ev.on(EV.DEFEAT, () => this.show('DEFEAT', '', 'bad', 1.6, 1.4));
  }

  show(big, sub, tone, dur, scale) {
    const el = document.createElement('div');
    el.className = 'banner ' + (tone || 'neutral');
    el.innerHTML = `<div class="big" style="transform:scale(${scale || 1})">${big}</div>${sub ? `<div class="sub">${sub}</div>` : ''}`;
    this.layer.appendChild(el);
    this.active.push({ el, t: 0, dur });
    // keep the stack short
    if (this.active.length > 4) { const old = this.active.shift(); old.el.remove(); }
  }

  _flash() {
    this.flash.style.opacity = '1';
    this._flashT = 0.5;
  }

  showEnd(win) {
    this.end.style.display = 'flex';
    this.end.classList.toggle('win', win);
    this.end.classList.toggle('lose', !win);
    // generated splash art behind the verdict text
    this.end.style.backgroundImage =
      `linear-gradient(rgba(3,2,2,0.55), rgba(3,2,2,0.8)), url('art/${win ? 'victory' : 'defeat'}.png')`;
    this.end.style.backgroundSize = 'cover';
    this.end.style.backgroundPosition = 'center';
    this.endH.textContent = win ? 'THE FORGE IS BROKEN' : 'THE HOST IS SCATTERED';
    this.endS.textContent = win
      ? 'Ourang-Vael lies dead beneath his own citadel. The Magmar Dynasty burns to cold ash. Benjen Daguth stands, Masamune black with their ruin.'
      : 'The Ashen Host falters. Benjen falls amid the embers, and the Magmar tide rolls on unbroken.';
  }

  update(world, dt) {
    // banner life-cycle
    for (let i = this.active.length - 1; i >= 0; i--) {
      const b = this.active[i];
      b.t += dt;
      const inA = Math.min(1, b.t / 0.25);
      const outA = Math.min(1, Math.max(0, (b.dur - b.t) / 0.5));
      const a = Math.min(inA, outA);
      b.el.style.opacity = a;
      b.el.style.transform = `translateY(${(1 - inA) * -20}px)`;
      if (b.t >= b.dur) { b.el.remove(); this.active.splice(i, 1); }
    }
    // titan flash fade
    if (this._flashT > 0) {
      this._flashT -= dt;
      if (this._flashT <= 0) this.flash.style.opacity = '0';
    }
    // screen desaturation while Titanfall storms
    const tf = world.player.state === 'titanfall';
    if (tf !== this._desat) {
      this._desat = tf;
      if (this._app) this._app.style.filter = tf ? 'grayscale(0.72) contrast(1.12) brightness(0.95)' : '';
    }
  }
}
